import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { abandonUnpaidReceipt } from "../../utils/APICalls/APICalls";
import { unholdWalletPayment } from "../../utils/APICalls/walletAPICalls";
import { PagesEnum } from "../../utils/enums";
import {
  clearPendingOnlinePayment,
  peekPendingOnlinePayment,
} from "../../utils/pendingOnlinePayment";

/**
 * Pages that are themselves part of a payment in flight, where a stored attempt
 * must be left alone.
 *
 * An allowlist rather than a single "not the success page" exception: `/pay` is
 * the gateway's success_url and owns its own cleanup, and `/payment-status` polls
 * a payment that has not resolved yet ("hang tight"). Cancelling an order from
 * either would release the rewards of a payment that is still settling. Today
 * nothing routes to `/payment-status` with a live stamp, but naming the pages
 * that mean "still paying" keeps that from turning into a silent bug if a
 * gateway return URL or a 3DS hand-back ever lands there.
 */
const PAYMENT_IN_PROGRESS_PATHS: readonly string[] = [
  `/${PagesEnum.Pay}`,
  `/${PagesEnum.PaymentStatus}`,
];

/**
 * What POST /receipt/{id}/abandon-unpaid answers. Not cancelled because it is
 * paid, or because the gateway shows a payment in flight, means the order may
 * still go through -- so whatever it holds must stay held.
 */
type AbandonUnpaidResult = {
  cancelled?: boolean;
  reason?: "paid" | "not_cancellable" | "in_flight_transaction";
};

const mayStillBePaid = (result: AbandonUnpaidResult | undefined) =>
  result?.cancelled === false &&
  (result.reason === "paid" || result.reason === "in_flight_transaction");

/**
 * Hands the wallet balance held against an abandoned order back to the
 * customer, as the app does. The backend releases it only on a failed Paymob
 * callback, not when the customer simply backs out. Called only once the
 * abandon has answered, never on a failed request. Best effort: a failure
 * leaves the hold for the wallet service to expire.
 */
const releaseWalletHold = (receiptId: number) => {
  unholdWalletPayment(receiptId).catch(() => undefined);
};

/**
 * Releases the points and offer held against an online order the customer walked
 * away from (mounted app-wide from BackgroundHooks).
 *
 * Confirming an online-card order sends the browser off to the payment gateway,
 * and stamps the attempt into sessionStorage on the way out. A stamp carrying a
 * `departedAtMs` means two things happened: we sent the customer to the gateway,
 * and this document was actually torn down to go there. Seeing that pair on a
 * page load means the customer is back on our site — and unless they are on a
 * page where the payment is still in flight, they are back WITHOUT having paid:
 * the back action, a Paymob redirect after a declined attempt, or the
 * payment-failed page.
 *
 * A stamp without a departure is not a return at all. It is the few seconds
 * between `storePendingOnlinePayment` and the browser leaving, where the page is
 * still alive and can mount or re-render — see the guard below.
 *
 * Backing out does not close the gateway page, though: it stays in the tab's
 * session history, one forward navigation away, and going back to it runs no
 * code of ours. Cancelling the order while that page stays reachable is how
 * customers got charged for orders that no longer existed (receipts 2189543,
 * 2192246, 2225895) — so the abandon is paired with a pushState that discards
 * the forward history, gateway page included. The customer's only route back to
 * payment is Pay again, through our own code.
 *
 * The remaining race — the payment that has already settled but whose webhook
 * has not reached the backend yet — is the backend's to close, and it does:
 * the cancellation is deferred server-side (ABANDON_UNPAID_CANCEL_DELAY_SECONDS)
 * and refused outright while the gateway shows a transaction for the receipt.
 *
 * The stamp is cleared only AFTER the order is actually cancelled. It doubles as
 * `usePayHelper`'s duplicate-order guard, so dropping it on a failed abandon
 * would leave the order alive and unguarded — pressing Pay again would then
 * confirm a second receipt and the restaurant would get two orders for one
 * purchase. Keeping it on failure means the retry reopens that still-live session
 * instead.
 *
 * A paid order is safe from this: the server refuses to cancel one, which covers
 * the gateway callback that lands while the customer is still walking back.
 */
export const useReleaseAbandonedPayment = () => {
  const router = useRouter();
  const isPaymentInProgressPage = PAYMENT_IN_PROGRESS_PATHS.includes(
    router.pathname
  );
  // The receipt currently being (or already) abandoned. Holds a receipt id
  // rather than a bare flag: the stamp is no longer read-and-cleared, so
  // something has to stop a bfcache restore firing a second abandon for the same
  // attempt -- while still letting a LATER attempt through. Pressing Pay again
  // creates order #2 with its own id, and that one must be abandonable too.
  const abandoning = useRef<number | null>(null);

  const release = useCallback(() => {
    if (isPaymentInProgressPage) {
      return;
    }

    const pending = peekPendingOnlinePayment();
    if (!pending?.receiptId || abandoning.current === pending.receiptId) {
      return;
    }

    // The stamp alone proves nothing: `usePayHelper` writes it one line before
    // the redirect, so it is already sitting in storage while the page is still
    // ours and the browser is only on its way out. Every mount, re-render and
    // restore in that window read it as "the customer is back without paying" —
    // which is how orders were cancelled 3 seconds after checkout while their
    // owner was still at the gateway, and then paid into (receipts 2313999,
    // 2317719).
    //
    // `departedAtMs` is the missing half: it is written from a `pagehide`, an
    // event a remount cannot fake, so it exists only if this document really was
    // torn down for the gateway. No departure, no trip, nothing to abandon.
    if (!pending.departedAtMs) {
      return;
    }

    const receiptId = pending.receiptId;
    abandoning.current = receiptId;
    // The order is being given up, so its gateway page has to stop being
    // reachable -- synchronously, before the request even leaves. Pushing a
    // history entry while the pointer is not at the tip discards every forward
    // entry, the gateway page among them. pushState, not replaceState:
    // replaceState swaps the current entry and leaves forward history intact,
    // which is exactly the route that has to go.
    //
    // Nobody is stranded by this. If the abandon below fails the stamp stays,
    // and pressing Pay again reopens the very same payment session through
    // `getReusablePendingOnlinePayment` -- a route through our own code, which
    // is the one we want them on.
    try {
      window.history.pushState(null, "", window.location.href);
    } catch {
      // Blocked history -- the abandon still runs, it just leaves the forward
      // entry in place. The backend's deferred cancel is the remaining guard.
    }
    abandonUnpaidReceipt(receiptId)
      .then((response) => {
        // Cancelled for real: the dead session may go, so pressing Pay again
        // issues a fresh confirm instead of reopening it.
        clearPendingOnlinePayment();
        if (pending.usesWallet && !mayStillBePaid(response?.data)) {
          releaseWalletHold(receiptId);
        }
      })
      .catch(() => {
        // A wallet order's hold stays too. A failed request says nothing about
        // the order -- it may have gone through -- and releasing the hold of a
        // live order hands the customer the same money twice. Only an abandon
        // that answered, and did not say the order may still be paid, releases
        // it; keeping the stamp lets the next signal ask again.
        //
        // Best effort. The order still carries its hold, so the rewards come back
        // when it expires — the customer just waits it out in this one case. The
        // stamp stays put, still guarding against a duplicate order, and the ref
        // is released so the next signal can retry.
        if (abandoning.current === receiptId) {
          abandoning.current = null;
        }
      });
  }, [isPaymentInProgressPage]);

  useEffect(() => {
    release();

    // Returning through the back/forward cache restores the page without
    // remounting, so the effect above never re-runs — `pageshow` is the only
    // signal for that trip back from the gateway.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        release();
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [release]);
};
